import '@mdi/font/css/materialdesignicons.css'
import 'vuetify/styles'
import '@/styles/shared/portal-list.css'
import '@/styles/shared/portal-list-cards.css'
import '@/styles/shared/panel-toolbar.css'
import '@/styles/shared/icon-containers.css'
import '@/styles/agent/index.css'
import '@/styles/dashboard/index.css'
import '@/styles/call-history/index.css'
import '@/styles/callers/index.css'
import '@/styles/transactions/index.css'
import '@/styles/users/index.css'
import '@/styles/api-keys/index.css'
import '@/styles/business-queries/index.css'
import '@/styles/contexts/index.css'
import '@/styles/portal-shell.css'
import '@/styles/portal-theme.css'
import '@/styles/portal-responsive.css'
import '@/styles/brand-buttons.css'
import '@/styles/theme-component-overrides.css'
import '@/styles/light-theme-overrides.css'
import '@/styles/light-theme-animations.css'
import '@/styles/login/index.css'
import { createVuetify } from 'vuetify'
import { VFileUpload } from 'vuetify/labs/VFileUpload'
import {
  portalDarkThemeColors,
  portalLightThemeColors,
  getStoredPortalTheme,
} from '@/utils/portalTheme'

export { portalDarkThemeColors as portalThemeColors }

export const portalLayout = {
  space: { 1: '0.25rem', 2: '0.5rem', 3: '0.75rem', 4: '1rem', 5: '1.25rem', 6: '1.5rem', 8: '2rem', 12: '3rem' },
  radius: { sm: '0.375rem', md: '0.5rem', lg: '0.75rem', xl: '1rem', full: '9999px' },
  shadow: {
    xs: '0 1px 2px rgba(7, 18, 16, 0.04)',
    sm: '0 2px 8px rgba(7, 18, 16, 0.06)',
    md: '0 8px 24px rgba(7, 18, 16, 0.08)',
    lg: '0 16px 48px rgba(7, 18, 16, 0.12)',
  },
  font: {
    sans: '"Poppins", system-ui, -apple-system, sans-serif',
    xs: '0.75rem',
    sm: '0.8125rem',
    base: '0.875rem',
    md: '1rem',
    lg: '1.125rem',
    xl: '1.25rem',
  },
  lineHeight: { tight: '1.25', normal: '1.5' },
  letterSpacing: { label: '0.04em' },
  table: { rowHeight: '2rem', headerHeight: '1.875rem' },
  field: { height: '1.875rem', fontSize: '0.8125rem' },
  transition: {
    fast: '150ms cubic-bezier(0.4, 0, 0.2, 1)',
    base: '220ms cubic-bezier(0.4, 0, 0.2, 1)',
  },
}

const PORTAL_CSS_VARS = `
:root,
.v-application {
  --y-space-1: ${portalLayout.space[1]};
  --y-space-2: ${portalLayout.space[2]};
  --y-space-3: ${portalLayout.space[3]};
  --y-space-4: ${portalLayout.space[4]};
  --y-space-5: ${portalLayout.space[5]};
  --y-space-6: ${portalLayout.space[6]};
  --y-space-8: ${portalLayout.space[8]};
  --y-space-12: ${portalLayout.space[12]};

  --y-radius-sm: ${portalLayout.radius.sm};
  --y-radius-md: ${portalLayout.radius.md};
  --y-radius-lg: ${portalLayout.radius.lg};
  --y-radius-xl: ${portalLayout.radius.xl};
  --y-radius-full: ${portalLayout.radius.full};

  --y-shadow-xs: ${portalLayout.shadow.xs};
  --y-shadow-sm: ${portalLayout.shadow.sm};
  --y-shadow-md: ${portalLayout.shadow.md};
  --y-shadow-lg: ${portalLayout.shadow.lg};

  --y-font-sans: ${portalLayout.font.sans};
  --y-font-size-xs: ${portalLayout.font.xs};
  --y-font-size-sm: ${portalLayout.font.sm};
  --y-font-size-base: ${portalLayout.font.base};
  --y-font-size-md: ${portalLayout.font.md};
  --y-font-size-lg: ${portalLayout.font.lg};
  --y-font-size-xl: ${portalLayout.font.xl};
  --y-line-height-tight: ${portalLayout.lineHeight.tight};
  --y-line-height-normal: ${portalLayout.lineHeight.normal};
  --y-letter-spacing-label: ${portalLayout.letterSpacing.label};

  --y-table-row-height: ${portalLayout.table.rowHeight};
  --y-table-header-height: ${portalLayout.table.headerHeight};

  --y-field-height: ${portalLayout.field.height};
  --y-field-font-size: ${portalLayout.field.fontSize};

  --y-transition-fast: ${portalLayout.transition.fast};
  --y-transition-base: ${portalLayout.transition.base};
}

.portal-app.v-application {
  background: var(--y-portal-scene-bg, var(--y-app-bg)) !important;
  color: var(--y-text);
}

.login-app.v-application {
  background: var(--y-login-scene-bg, var(--y-app-bg)) !important;
  height: 100vh;
  height: 100dvh;
  max-height: 100vh;
  max-height: 100dvh;
  min-height: -webkit-fill-available;
  overflow: hidden;
}

.portal-app .main-bg.v-main {
  background: transparent !important;
}

.portal-app .sidebar.v-navigation-drawer {
  background: var(--y-sidebar-bg) !important;
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border-right: 1px solid var(--y-sidebar-border) !important;
  box-shadow: 4px 0 24px rgba(0, 0, 0, 0.08);
}

.sidebar.v-navigation-drawer {
  background: rgb(var(--v-theme-shell-from)) !important;
  border-right: 1px solid rgb(var(--v-theme-shell-border)) !important;
  box-shadow: none;
}

.sidebar .v-list-item .v-list-item__overlay,
.sidebar .v-list-item--active .v-list-item__overlay {
  opacity: 0 !important;
}

.portal-app .sidebar .sidebar-item.v-list-item:hover {
  background: var(--y-sidebar-item-hover-bg) !important;
  color: var(--y-sidebar-text) !important;
}

.portal-app .sidebar .sidebar-item.v-list-item:hover .v-list-item__prepend > .v-icon {
  color: var(--y-sidebar-icon-hover) !important;
}

.portal-app .sidebar .sidebar-item.v-list-item--active,
.portal-app .sidebar .active-sidebar-item {
  background: var(--y-sidebar-active-bg) !important;
  color: var(--y-sidebar-text-active) !important;
  box-shadow: var(--y-sidebar-active-shadow, none) !important;
}

.sidebar .sidebar-item.v-list-item:hover {
  background: rgb(var(--v-theme-shell-hover)) !important;
  color: rgb(var(--v-theme-text-inverse)) !important;
}

.sidebar .sidebar-item.v-list-item--active,
.sidebar .active-sidebar-item {
  background: rgb(var(--v-theme-shell-active)) !important;
  color: rgb(var(--v-theme-text-inverse)) !important;
}

.portal-app .sidebar .sidebar-item--logout.v-list-item:hover {
  background: var(--y-sidebar-logout-hover-bg) !important;
  color: var(--y-sidebar-logout-hover-text) !important;
}

.sidebar .sidebar-item--logout.v-list-item:hover {
  background: rgb(var(--v-theme-shell-hover)) !important;
  color: rgb(var(--v-theme-error)) !important;
}

.portal-app .app-bar-search .v-field {
  background: var(--y-glass-input-bg) !important;
  color: var(--y-field-text) !important;
  border-radius: var(--y-radius-lg) !important;
}

.portal-app .app-bar-search .v-field__outline {
  --v-field-border-opacity: 0.45;
  color: var(--y-glass-input-border) !important;
}

.portal-app .app-bar-search .v-field:hover {
  background: var(--y-glass-surface-hover) !important;
}

.portal-app .app-bar-search .v-field--focused {
  background: var(--y-glass-surface-hover) !important;
  box-shadow: var(--y-field-focus-ring) !important;
}

.portal-app .app-bar-search .v-field--focused .v-field__outline {
  color: var(--y-glass-input-focus) !important;
  --v-field-border-opacity: 1;
}

.portal-app .app-bar-search .v-field__input,
.portal-app .app-bar-search input {
  color: var(--y-field-text) !important;
}

.portal-app .app-bar-search .v-field__input::placeholder,
.portal-app .app-bar-search input::placeholder {
  color: var(--y-field-placeholder) !important;
  opacity: 1;
}

.portal-app .app-bar-search .v-icon {
  color: var(--y-field-icon) !important;
}

.app-bar-search .v-field {
  background: rgb(var(--v-theme-background)) !important;
  color: rgb(var(--v-theme-on-background)) !important;
}

.app-bar-search .v-field:hover {
  background: rgb(var(--v-theme-surface-hover)) !important;
}

.app-bar-search .v-field--focused {
  background: rgb(var(--v-theme-surface)) !important;
  box-shadow: var(--y-shadow-focus) !important;
}

.app-bar-search .v-field__input,
.app-bar-search input::placeholder {
  color: rgb(var(--v-theme-on-background)) !important;
}

.app-bar-search .v-field__input::placeholder {
  color: rgb(var(--v-theme-text-muted)) !important;
}

.app-bar-search .v-icon {
  color: rgb(var(--v-theme-text-muted)) !important;
}

.portal-app .v-btn.bg-primary,
.portal-app .btn-primary-cta.v-btn {
  background: var(--y-btn-primary-gradient) !important;
  color: #ffffff !important;
  box-shadow: var(--y-btn-primary-shadow) !important;
}

.portal-app .v-btn.bg-primary:hover:not(:disabled),
.portal-app .btn-primary-cta:hover:not(:disabled) {
  filter: brightness(1.06);
  box-shadow: var(--y-btn-primary-shadow-hover) !important;
}

.v-application .v-btn.bg-primary,
.v-application .btn-primary-cta.v-btn {
  background: rgb(var(--v-theme-primary)) !important;
  color: #fff !important;
}

.v-application .v-btn.bg-primary:hover:not(:disabled),
.v-application .btn-primary-cta:hover:not(:disabled) {
  background: rgb(var(--v-theme-primary-darken-1)) !important;
}

.portal-app .table-container .app-data-table.v-table tbody > tr:hover > td,
.portal-app .table-container .app-data-table .v-data-table__tr--hover > td {
  background: var(--y-table-row-hover) !important;
  color: var(--y-table-body-text) !important;
}

.portal-app .table-container .app-data-table.v-table tbody > tr:hover {
  --v-hover-opacity: 0;
}

.table-container .app-data-table.v-table tbody > tr:hover > td,
.table-container .app-data-table .v-data-table__tr--hover > td {
  background: rgb(var(--v-theme-table-row-hover)) !important;
  color: rgb(var(--v-theme-on-background)) !important;
}

.table-container .app-data-table.v-table tbody > tr:hover {
  --v-hover-opacity: 0;
}

.portal-app .content-card .v-field {
  background: var(--y-field-bg);
  color: var(--y-field-text);
}

.portal-app .content-card .v-field__outline {
  --v-field-border-opacity: 0.45;
  color: var(--y-field-border);
}

.portal-app .content-card .v-field--focused .v-field__outline {
  color: var(--y-field-focus-border);
  --v-field-border-opacity: 1;
}

.portal-app .content-card .v-field--focused {
  box-shadow: var(--y-field-focus-ring);
}

.portal-app .content-card .v-field__input,
.portal-app .content-card input,
.portal-app .content-card textarea {
  color: var(--y-field-text);
}

.portal-app .content-card .v-label {
  color: var(--y-field-label) !important;
}

.portal-app .content-card .v-field--disabled {
  opacity: 0.65;
}

.user-dialog-card .v-field {
  background: var(--y-field-bg);
  color: var(--y-field-text);
}

.user-dialog-card .v-field__outline {
  --v-field-border-opacity: 0.45;
  color: var(--y-field-border);
}

.user-dialog-card .v-field--focused .v-field__outline {
  color: var(--y-field-focus-border);
  --v-field-border-opacity: 1;
}

.user-dialog-card .v-field--focused {
  box-shadow: var(--y-field-focus-ring);
}

.user-dialog-card .v-field__input,
.user-dialog-card input,
.user-dialog-card textarea {
  color: var(--y-field-text);
}

.user-dialog-card .v-label {
  color: var(--y-field-label) !important;
}

.user-dialog-card .btn-primary-cta.v-btn {
  background: var(--y-btn-primary-gradient) !important;
  color: #ffffff !important;
  box-shadow: var(--y-btn-primary-shadow) !important;
}

.user-dialog-card .btn-primary-cta.v-btn:hover:not(:disabled) {
  filter: brightness(1.06);
  box-shadow: var(--y-btn-primary-shadow-hover) !important;
}
`

function injectPortalCssVars() {
  if (typeof document === 'undefined') return
  const id = 'yandle-portal-theme-vars'
  let el = document.getElementById(id)
  if (!el) {
    el = document.createElement('style')
    el.id = id
    document.head.appendChild(el)
  }
  el.textContent = PORTAL_CSS_VARS
}

injectPortalCssVars()

export default createVuetify({
  components: {
    VFileUpload,
  },
  theme: {
    defaultTheme: getStoredPortalTheme(),
    themes: {
      dark: {
        dark: true,
        colors: portalDarkThemeColors,
        variables: {
          'font-family': portalLayout.font.sans,
          'border-radius-root': '8px',
        },
      },
      light: {
        dark: false,
        colors: portalLightThemeColors,
        variables: {
          'font-family': portalLayout.font.sans,
          'border-radius-root': '8px',
        },
      },
    },
  },
  defaults: {
    VBtn: {
      rounded: 'lg',
      elevation: 0,
      size: 'small',
    },
    VCard: {
      rounded: 'lg',
      elevation: 0,
    },
    VTextField: {
      variant: 'outlined',
      density: 'compact',
      color: 'primary',
      hideDetails: 'auto',
    },
    VSelect: {
      variant: 'outlined',
      density: 'compact',
      color: 'primary',
    },
    VChip: {
      rounded: 'lg',
      size: 'small',
    },
    VDataTable: {
      density: 'compact',
      hover: true,
    },
  },
})
